"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CancelSubscriptionButton } from "@/components/ui/CancelSubscriptionButton"
import { ResumeSubscriptionButton } from "@/components/ui/ResumeSubscriptionButton"
import { Badge } from "@/components/ui/badge"
import { Check, X, RefreshCw } from "lucide-react"
import { useLanguage } from "@/contexts/language-context"
import { useAuth } from "@/contexts/auth-context"
import { LoginModal } from "@/components/auth"
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { loadStripe } from "@stripe/stripe-js"
import { createClientSupabaseClient } from "@/supabase/client"

export function Pricing() {
  const { t } = useLanguage()
  const { isAuthenticated, user, userSubscription, refreshUserSubscription } = useAuth()
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [billingCycle, setBillingCycle] = useState<'annual' | 'monthly'>('annual')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null)
  const [isCancellingDowngrade, setIsCancellingDowngrade] = useState(false) // moved from inside renderPlanNotices to keep stable hook order
  const [showRepliesModal, setShowRepliesModal] = useState(false)
  const [oneTimeAmount, setOneTimeAmount] = useState<number>(20)
  const oneTimeExamples = [5,20,100,500]
  const { toast } = useToast()

  const supabase = createClientSupabaseClient()

  // 懒加载 Stripe 对象（仅当需要 SCA 确认时）
  let stripePromise: Promise<import("@stripe/stripe-js").Stripe | null> | null = null
  const getStripe = () => {
    if (!stripePromise) {
      stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "")
    }
    return stripePromise
  }

  // 统一订阅 / 升级入口
  const checkout = async (priceId: string, planId: string) => {
    if (!user?.email || !user?.id || !userSubscription) {
      console.error('User email or ID not available')
      return
    }
    // console.log('user: ', user, '; userSubscription: ', userSubscription)
    // console.log('userSubscription: ', userSubscription)

    setIsSubscribing(planId)

    const isUpgrade = (userSubscription.plan !== 'free' && userSubscription.price_id)

    const stripeAction = isUpgrade ? 'upgrade' : 'checkout'
    // 新 upgrade 接口仅需 newPriceId；checkout 仍需 email/priceId
    const stripeBody = isUpgrade
      ? { newPriceId: priceId }
      : { email: user.email, priceId, userId: user.id }
    try {
      const res = await fetch(`/api/stripe/${stripeAction}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(stripeBody),
      })
      const raw = await res.text()
      let data: any = null
      try {
        data = raw ? JSON.parse(raw) : null
      } catch {
        data = raw
      }
      if (!res.ok) {
        setIsSubscribing(null)
        const serverMessage =
          data && typeof data === 'object' && 'error' in data ? (data as any).error : undefined
        throw new Error(serverMessage || `HTTP error! status: ${res.status}`)
      }
      if (!isUpgrade) {
        if (!data || typeof data !== 'object') throw new Error('Invalid checkout response')
        if (data.url) {
          window.location.href = data.url
          return
        }
      } else {
        if (!data || typeof data !== 'object') throw new Error('Invalid upgrade response')
        if (data.error) {
          console.error('Upgrade error:', data.error, data.stripe || '')
        } else if (data.status === 'noop') {
          console.info('Already on target plan')
        } else if (data.status === 'upgraded') {
          if (data.requiresAction && data.clientSecret) {
            try {
              const stripe = await getStripe()
              if (stripe) {
                const confirm = await stripe.confirmCardPayment(data.clientSecret)
                if (confirm.error) console.error('Payment confirmation error:', confirm.error.message)
              }
            } catch (e) {
              console.error('Stripe SCA confirm failed:', e)
            }
          }
          if (data.mode === 'upgrade-immediate') {
            toast({ title: t('pricing.upgradeImmediateTitle'), description: t('pricing.upgradeImmediateDesc') })
          } else if (data.mode === 'downgrade-next-cycle') {
            toast({ title: t('pricing.downgradeScheduledTitle'), description: t('pricing.downgradeScheduledDesc') })
          }
        }
      }
    } catch (error) {
      console.error(isUpgrade ? 'Upgrade error:' : 'Checkout error:', error)
    } finally {
      setTimeout(() => {
        setIsSubscribing(null)
        handleRefreshSubscription()
      }, 1500)
    }
  }

  // 获取Stripe Price ID
  const getStripePriceId = (planId: string, cycle: 'annual' | 'monthly'): string => {
    const priceIds = {
      pro: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_PRO_MONTHLY || '',
        annual: process.env.NEXT_PUBLIC_STRIPE_PRICE_PRO_YEARLY || ''
      },
      max: {
        monthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_MAX_MONTHLY || '',
        annual: process.env.NEXT_PUBLIC_STRIPE_PRICE_MAX_YEARLY || ''
      }
    }

    return priceIds[planId as keyof typeof priceIds]?.[cycle] || ''
  }

  // 定义价格配置类型
  interface PriceInfo {
    price: string
    billing: string
    save?: string
  }

  interface PricingConfig {
    free: {
      monthly: PriceInfo
      annual: PriceInfo
    }
    pro: {
      monthly: PriceInfo
      annual: PriceInfo
    }
    max: {
      monthly: PriceInfo
      annual: PriceInfo
    }
  }

  // 定义价格配置
  const pricingConfig: PricingConfig = {
    free: {
      monthly: { price: "$0", billing: "" },
      annual: { price: "$0", billing: "" }
    },
    pro: {
      monthly: { price: "$9.99", billing: "Billed monthly" },
      annual: { price: "$6.99", billing: "$83.88 billed annually", save: "SAVE 30%" }
    },
    max: {
      monthly: { price: "$1800", billing: "Billed monthly" },
      annual: { price: "$1080", billing: "$12960 billed annually", save: "SAVE 40%" }
    }
  }

  // 定义功能配置，包含图标类型和间距
  const planFeatures = {
    free: [
      { text: t("pricing.free.feature1"), icon: "check", spacing: "large" }, // 10 replies per month - 需要大间距
      { text: t("pricing.free.feature2"), icon: "check", spacing: "normal" }, // Basic scenario
      { text: t("pricing.free.feature4"), icon: "check", spacing: "normal" }, // Historical reply records
      { text: t("pricing.free.feature3"), icon: "x", spacing: "normal" }, // Templates cannot be used
    ],
    pro: [
      { text: t("pricing.pro.feature1"), icon: "check", spacing: "normal" }, // 200 replies per month
      { text: t("pricing.pro.feature2"), icon: "check", spacing: "normal" }, // Basic+Custom scenario
      { text: t("pricing.pro.feature4"), icon: "check", spacing: "normal" }, // Historical reply records
      { text: t("pricing.pro.feature3"), icon: "check", spacing: "normal" }, // Define & Use templates
      //{ text: t("pricing.pro.feature5"), icon: "check", spacing: "normal" }, // Advanced customization
    ],
    max: [
      { text: t("pricing.business.feature1"), icon: "check", spacing: "normal" }, // Unlimited replies
      { text: t("pricing.business.feature3"), icon: "check", spacing: "large" }, // Basic+Custom scenario - 需要大间距
      { text: t("pricing.business.feature5"), icon: "check", spacing: "normal" }, // Historical reply records
      { text: t("pricing.business.feature4"), icon: "check", spacing: "normal" }, // Define & Use templates
      { text: t("pricing.business.feature6"), icon: "check", spacing: "normal" }, // Advanced customization
    ]
  }

  const plans = [
    {
      id: "free",
      name: t("pricing.plan.free"),
      features: planFeatures.free,
      cta: t("pricing.cta.trial"),
      popular: false,
    },
    {
      id: "pro",
      name: t("pricing.plan.pro"),
      features: planFeatures.pro,
      cta: t("pricing.cta.subscribe"),
      popular: true,
    },
    {
      id: "max",
      name: t("pricing.plan.business"),
      features: planFeatures.max,
      cta: t("pricing.cta.subscribe"),
      popular: false,
    },
  ]

  // 获取用户当前计划
  const getCurrentPlan = () => {
    if (!isAuthenticated || !userSubscription) return "free"
    return userSubscription.plan || "free"
  }
  const currentPlan = getCurrentPlan()

  // 获取用户当前计划
  const getCurrentPlanId = () => {
    switch (currentPlan) {
      case "pro-monthly":
      case "pro-yearly":
        return "pro"
      case "max-monthly":
      case "max-yearly":
        return "max"
      default:
        return "free"
    }
  }
  const currentPlanId = getCurrentPlanId()
  const pendingPlanVariant = userSubscription?.pending_plan as string | undefined
  const hasPendingDowngrade = !!pendingPlanVariant && pendingPlanVariant !== currentPlan

  const formatPendingPlanName = (variant?: string) => {
    if (!variant) return ''
    switch (variant) {
      case 'pro-monthly': return t('pricing.planName.proMonthly')
      case 'pro-yearly': return t('pricing.planName.proYearly')
      case 'max-monthly': return t('pricing.planName.maxMonthly')
      case 'max-yearly': return t('pricing.planName.maxYearly')
      case 'free': return t('pricing.planName.free')
      default: return variant
    }
  }
  
  // 获取planId对应计划
  const getPlanByIdAndBillingCycle = (planId: string, cycle: 'annual' | 'monthly'): string => {
    const priceIds = {
      free: {
        monthly: 'free',
        annual: 'free'
      },
      pro: {
        monthly: 'pro-monthly',
        annual: 'pro-yearly'
      },
      max: {
        monthly: 'max-monthly',
        annual: 'max-yearly'
      }
    }

    return priceIds[planId as keyof typeof priceIds]?.[cycle] || ''
  }

  // 处理按钮点击
  const handleButtonClick = (planId: string) => {
    if (!isAuthenticated) { setIsLoginModalOpen(true); return }
    if (!planId) return

    // 识别目标 variant
    const targetVariant = getPlanByIdAndBillingCycle(planId, billingCycle)
    const currentVariant = currentPlan
    const priceId = getStripePriceId(planId, billingCycle)

    // 点击当前付费计划（非 max） -> 购买一次性 replies
    if (planId !== 'free' && planId === currentPlanId && planId !== 'max') { setShowRepliesModal(true); return }

    // 免费计划不操作
    if (planId === 'free') return

    if (!priceId) { console.error('Missing price id for', planId, billingCycle); return }

    // 判断升降级（使用 rank）
    const upgrading = (() => {
      const rank = (v: string) => v.startsWith('max') ? 2 : v.startsWith('pro') ? 1 : 0
      return rank(targetVariant) > rank(currentVariant)
    })()

    // 如果与当前 variant 不同，则弹确认
    if (targetVariant !== currentVariant) {
      setConfirmAction({ targetPlanId: planId, targetVariant, priceId, upgrading })
      return
    }

    // 兜底
    checkout(priceId, planId)
  }

  // 获取按钮文本
  const getButtonText = (plan: any) => {
    if (isSubscribing === plan.id) {
      return t('pricing.processing')
    }
    
    if (!isAuthenticated) return plan.cta
    const userPlan = userSubscription?.plan
    if (plan.id === 'free' && userPlan === 'free') return t('pricing.cta.current')
    // 当前付费计划按钮显示“Get More Replies”
    if (currentPlanId === 'pro' && plan.id !== 'max') {
      return t('pricing.getMoreReplies')
    }
    if (plan.id === 'max') {
      if (
        (userPlan === 'max-monthly' && billingCycle === 'monthly') ||
        (userPlan === 'max-yearly' && billingCycle === 'annual')
      ) {
        return t('pricing.cta.current')
      }
    }
    return plan.cta
  }

  // 获取按钮样式
  const getButtonStyle = (plan: any) => {
    const userPlan = userSubscription?.plan
    const isCurrentPlan = isAuthenticated && (
      (plan.id === 'free' && userPlan === 'free') ||
      (plan.id === 'pro' && (
        (userPlan === 'pro-monthly' && billingCycle === 'monthly') ||
        (userPlan === 'pro-yearly' && billingCycle === 'annual')
      )) ||
      (plan.id === 'max' && (
        (userPlan === 'max-monthly' && billingCycle === 'monthly') ||
        (userPlan === 'max-yearly' && billingCycle === 'annual')
      ))
    )
    const isFreePlan = plan.id === 'free'
    const isProcessing = isSubscribing === plan.id
    
    if (isCurrentPlan && plan.id === 'max') {
      // max当前计划：灰色
      return "!bg-gray-200 dark:!bg-gray-600 !text-white dark:!text-white !border-gray-400 dark:!border-gray-600 cursor-not-allowed disabled:opacity-50"
    }
    
    if (isProcessing) {
      // 处理中：禁用状态
      return "!bg-gray-400 dark:!bg-gray-600 !text-white dark:!text-white !border-gray-400 dark:!border-gray-600 cursor-not-allowed"
    }
    
    if (isFreePlan) {
      // Free计划：outline样式
      //return "!bg-gray-100 dark:!bg-zinc-700 !border-gray-300 dark:!border-zinc-600 !text-gray-900 dark:!text-white hover:!bg-gray-50 dark:hover:!bg-zinc-600"
    }
    
    // Pro和Max计划：蓝色样式
    return "!bg-blue-600 hover:!bg-blue-700 dark:!bg-indigo-500 dark:hover:!bg-indigo-600 !text-white dark:!text-white !border-blue-600 dark:!border-indigo-500"
  }

  // 判断是否显示按钮
  const shouldShowButton = (plan: any) => {
    const userPlan = userSubscription?.plan
    if (plan.id !== 'free' && !isAuthenticated) return false
    if (plan.id === 'free' && isAuthenticated) return false

    //if (plan.id === 'pro') {
    //  if (
    //    (userPlan === 'pro-monthly' && billingCycle === 'annual') ||
    //    (userPlan === 'pro-yearly' && billingCycle === 'monthly')
    //  ) {
    //    return false
    //  }
    //}

    //if (plan.id === 'max') {
    //  if (
    //    (userPlan === 'max-monthly' && billingCycle === 'annual') ||
    //    (userPlan === 'max-yearly' && billingCycle === 'monthly')
    //  ) {
    //    return false
    //  }
    //}
    return true
  }

  // 获取价格信息
  const getPriceInfo = (planId: string) => {
    return pricingConfig[planId as keyof typeof pricingConfig][billingCycle]
  }

  // 检查是否需要显示"Unused replies don't roll over"
  const shouldShowRolloverNote = (planId: string, feature: any) => {
    return (planId === 'free' || planId === 'pro') && (feature.text.includes(t("pricing.pro.feature1")) || feature.text.includes(t("pricing.free.feature1")))
  }

  // 渲染功能图标
  const renderFeatureIcon = (iconType: string) => {
    if (iconType === "x") {
      return <X className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0" />
    }
    return <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-3 flex-shrink-0" />
  }

  // 获取功能文本样式
  const getFeatureTextStyle = (planId: string, iconType: string) => {
    const baseStyle = "text-sm text-gray-700 dark:text-gray-100 !important"
    // 为Free计划中标记X的项目添加删除线
    if (planId === "free" && iconType === "x") {
      return `${baseStyle} line-through opacity-75`
    }
    return baseStyle
  }

  // 获取计划显示名称
  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free':
        return t('pricing.planName.free')
      case 'pro-monthly':
        return t('pricing.planName.proMonthly')
      case 'pro-yearly':
        return t('pricing.planName.proYearly')
      case 'max-monthly':
        return t('pricing.planName.maxMonthly')
      case 'max-yearly':
        return t('pricing.planName.maxYearly')
      default:
        return t('pricing.planName.free')
    }
  }

  // 格式化日期
  const formatDate = (timestamp: string | undefined) => {
    if (!timestamp) return ''
    try {
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return ''
    }
  }

  // 获取当前订阅的开始时间和一年后的时间
  // console.log('Current Subscription:', userSubscription);
  const now = Date.now()
  // 统一的取消日期（修正 yearly 但 current_period_end 仅一个月的情况）
  const computeDisplayCancelDate = () => {
    if (!userSubscription?.current_period_end) return ''
    const plan = userSubscription.plan || ''
    const rawEnd = new Date(userSubscription.current_period_end)
    const start = userSubscription.current_period_start ? new Date(userSubscription.current_period_start) : null
    if (plan.endsWith('-yearly') && start) {
      const diffDays = (rawEnd.getTime() - start.getTime()) / 86400000
      if (diffDays < 330) { // 明显不是一年（可能是之前的合成或错误写入）
        const corrected = new Date(start)
        corrected.setFullYear(corrected.getFullYear() + 1)
        return formatDate(corrected.toISOString())
      }
    }
    return formatDate(userSubscription.current_period_end)
  }
  const displayCancelDate = computeDisplayCancelDate()

  // 刷新用户订阅信息
  const handleRefreshSubscription = async () => {
    if (!isAuthenticated || isRefreshing) return
    
    setIsRefreshing(true)
    try {
      if (userSubscription) {
        let plan = userSubscription.plan
        let end = userSubscription.current_period_end ? new Date(userSubscription.current_period_end).getTime() : 0
        const endedAt = userSubscription.ended_at ? new Date(userSubscription.ended_at).getTime() : null
        let updateData: any = {}

        if (end <= now) {
          if (!endedAt || endedAt > now) {
            let newEnd = new Date(end)
            do { newEnd.setMonth(newEnd.getMonth() + 1) } while (newEnd.getTime() <= now)
            const qty = plan.startsWith('max') ? 99999 : plan.startsWith('pro') ? 200 : 10
            updateData = { current_period_end: newEnd.toISOString(), quantity: qty }
          } else {
            plan = 'free'
            let newEnd = new Date(end)
            do { newEnd.setMonth(newEnd.getMonth() + 1) } while (newEnd.getTime() <= now)
            updateData = { plan: 'free', current_period_end: newEnd.toISOString(), quantity: 10, ended_at: null }
          }
        }

        if (Object.keys(updateData).length) {
          await supabase.from('user_subscriptions').update(updateData).eq('user_id', user!.id)
        }
      }
      await refreshUserSubscription()
    } catch (error) {
      console.error('Failed to refresh subscription:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  // 取消或降级提示文案
  const renderPlanNotices = () => {
    if (!userSubscription) return null
  const { cancel_at_period_end, current_period_end, status, pending_plan, pending_apply_at } = userSubscription as any
  const downgradeScheduled = !!pending_plan && pending_plan !== userSubscription.plan
  // 取消到期提示已在上方主区域展示，这里不再重复
    const pendingDate = pending_apply_at ? formatDate(pending_apply_at) : ''

    return (
      <div className="space-y-2 mt-2">
        {downgradeScheduled && (
          <div className="text-xs text-purple-600 dark:text-purple-300 font-medium flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
            {t('pricing.downgradeScheduled')} {formatPendingPlanName(pending_plan)} {t('pricing.on')} {pendingDate}
            <button
              onClick={async () => {
                if (isCancellingDowngrade) return;
                setIsCancellingDowngrade(true)
                try {
                  await fetch('/api/stripe/downgrade-cancel', { method: 'POST' })
                  await refreshUserSubscription()
                  toast({ title: t('pricing.pendingDowngradeCanceledTitle'), description: t('pricing.pendingDowngradeCanceledDesc') })
                } catch (e) {
                  console.error(e)
                  toast({ title: t('pricing.pendingDowngradeCancelFailedTitle'), description: t('pricing.pendingDowngradeCancelFailedDesc') })
                } finally {
                  setTimeout(() => setIsCancellingDowngrade(false), 600) // 轻微延迟防闪烁
                }
              }}
              className="ml-2 underline disabled:opacity-60"
              disabled={isCancellingDowngrade}
            >
              {isCancellingDowngrade ? t('pricing.cancelPendingDowngradeProcessing') : t('pricing.cancelPendingDowngrade')}
            </button>
          </div>
        )}
      </div>
    )
  }

  const [confirmAction, setConfirmAction] = useState<null | { targetPlanId: string; targetVariant: string; priceId: string; upgrading: boolean }>(null)

  return (
    <>
  <section id="pricing" className="py-20">
        <div className="container px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("pricing.title")}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
              {t("pricing.subtitle")}
            </p>
            
            {/* Current Plan Display - Only show for authenticated users */}
            {isAuthenticated && userSubscription && (
              <div className="max-w-4xl mx-auto mb-8">
                {/* Current Plan Title - Outside the box */}
                <div className="text-left mb-2">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("pricing.currentPlan")} {getPlanDisplayName(userSubscription.plan || 'free')}
                  </span>
                  <span className="flex flex-col text-sm text-gray-600 dark:text-gray-300">
                    {(() => {
                      const scheduled = !!(userSubscription as any).cancel_at_period_end;
                      const endDate = displayCancelDate;
                      return scheduled ? (
                        <span>
                          {t('pricing.willCancelOn')} {endDate}
                          <span className="pl-2 text-sm"><ResumeSubscriptionButton /></span>
                        </span>
                      ) : (
                        <span>
                          {t('pricing.autoRenewalDate')}{endDate}
                          <span className="pl-2 text-sm">{userSubscription.plan !== 'free' && !userSubscription.pending_plan ? <CancelSubscriptionButton /> : null}</span>
                        </span>
                      )
                    })()}
                    {renderPlanNotices()}
                  </span>
                </div>
                
                {/* Main Info Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Left Side - Answers Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                          {t("pricing.answersLeft")} {userSubscription.quantity || 0}
                        </span>
                        <button
                          onClick={handleRefreshSubscription}
                          disabled={isRefreshing}
                          className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                          title="Refresh subscription info"
                        >
                          <RefreshCw 
                            className={`w-3 h-3 text-blue-600 dark:text-blue-300 ${isRefreshing ? 'animate-spin' : ''}`} 
                          />
                        </button>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 text-left">
                        {t("pricing.autoRenews")} {formatDate(userSubscription.current_period_end)}
                      </div>
                    </div>
                    
                    {/* Right Side - Upgrade Info */}
                    <div className="text-right lg:text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("pricing.needMore")}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {t("pricing.upgradeBelow")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center mb-8">
              <div className="bg-white dark:bg-zinc-800 rounded-full p-1 border border-gray-200 dark:border-zinc-700 shadow-sm">
                <div className="flex items-center">
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`flex items-center px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'annual'
                        ? 'bg-blue-300 dark:bg-indigo-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <span>Annual</span>
                    <span className={`ml-2 text-xs font-bold transition-colors duration-200 ${
                      billingCycle === 'annual' 
                        ? 'text-green-300' 
                        : 'text-green-400'
                    } drop-shadow-sm`} 
                    style={{ 
                      textShadow: billingCycle === 'annual' ? '0 0 8px rgba(34, 197, 94, 0.6)' : '0 0 8px rgba(34, 197, 94, 0.8)' 
                    }}>
                      Best Value
                    </span>
                  </button>
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'monthly'
                        ? 'bg-blue-300 dark:bg-indigo-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => {
              const priceInfo = getPriceInfo(plan.id)
              
              return (
                <Card key={index} className={`hover-glow-card relative bg-white dark:bg-zinc-800/95 border-gray-200 dark:border-zinc-700 ${plan.popular ? "border-blue-500 dark:border-indigo-500 shadow-lg scale-105" : ""}`}>
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-300 dark:bg-indigo-500 text-white dark:text-white">{t("pricing.popular")}</Badge>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl text-gray-900 dark:text-white !important">{plan.name}</CardTitle>
                    <div className="mt-6">
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white !important">{priceInfo.price}</span>
                        {plan.id !== 'free' && (
                          <span className="text-gray-600 dark:text-gray-200 !important ml-1">/month</span>
                        )}
                      </div>
                      
                      {/* Billing Information */}
                      {priceInfo.billing && (
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {priceInfo.billing}
                          </span>
                          {priceInfo.save && (
                            <span className="transition-colors text-green-300 text-xs px-2 py-1 rounded-full font-bold drop-shadow-sm" style={{textShadow: '0 0 8px rgba(34, 197, 94, 0.6)'}}>
                              {priceInfo.save}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6 flex flex-col pt-2">
                    <ul className="space-y-3 flex-1">
                      {plan.features.map((feature: any, featureIndex: number) => (
                        <li key={featureIndex} className="space-y-1">
                          <div className={`flex items-center ${feature.spacing === 'large' ? 'mt-6' : ''}`}>
                            {renderFeatureIcon(feature.icon)}
                            <span className={getFeatureTextStyle(plan.id, feature.icon)}>{feature.text}</span>
                          </div>
                          {shouldShowRolloverNote(plan.id, feature) && (
                            <div className="ml-7">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t("pricing.doNotRollOver")}
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      {shouldShowButton(plan) && (
                        (() => {
                          const targetVariant = getPlanByIdAndBillingCycle(plan.id, billingCycle)
                          const isPendingTarget = hasPendingDowngrade && pendingPlanVariant === targetVariant
                          if (isPendingTarget) {
                            return (
                              <Button
                                className={`w-full h-12 !important ${getButtonStyle(plan)}`}
                                variant={plan.id === 'free' ? 'outline' : 'default'}
                                size="lg"
                                disabled
                              >
                                {t('pricing.scheduled')}
                              </Button>
                            )
                          }
                          return (
                            <Button 
                              className={`w-full h-12 !important ${getButtonStyle(plan)}`}
                              variant={plan.id === 'free' ? 'outline' : 'default'} 
                              size="lg"
                              onClick={() => handleButtonClick(plan.id)}
                              disabled={isSubscribing === plan.id || (currentPlan === getPlanByIdAndBillingCycle(plan.id, billingCycle) && plan.id !== 'pro' && plan.id !== 'free')}
                            >
                              {getButtonText(plan)}
                            </Button>
                          )
                        })()
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>
      
      {/* 登录模态框 */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      {showRepliesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-2xl shadow-xl p-8 relative">
            <h3 className="text-xl font-semibold mb-6 text-white">{t('pricing.oneTimeTitle')}</h3>
            <div className="mb-6">
              <label className="block text-sm text-gray-300 mb-2">{t('pricing.oneTimeAmountLabel')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={500}
                  step={1}
                  value={oneTimeAmount}
                  onChange={e=> setOneTimeAmount(Math.min(500, Math.max(1, Number(e.target.value)||1)))}
                  className="flex-1 bg-zinc-800 border border-zinc-600 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-400">USD =</span>
                <span className="text-xl font-bold text-white tabular-nums">{(oneTimeAmount*20).toLocaleString()} {t('pricing.repliesUnit')}</span>
              </div>
              <div className="flex gap-3 mt-5 flex-wrap">
                {oneTimeExamples.map(v => (
                  <button
                    key={v}
                    onClick={()=> setOneTimeAmount(v)}
                    className={`px-5 py-2 rounded-md border text-sm font-medium transition ${oneTimeAmount===v ? 'bg-zinc-700 border-zinc-500 text-white' : 'bg-zinc-800 border-zinc-700 text-gray-300 hover:border-zinc-500'}`}
                  >${v}</button>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-8">{t('pricing.oneTimeNotice')}</p>
            <div className="flex justify-end gap-4">
              <button onClick={()=> setShowRepliesModal(false)} className="px-5 h-11 rounded-md bg-zinc-800 text-gray-300 hover:bg-zinc-700 text-sm font-medium">{t('pricing.cancel')}</button>
              <button onClick={async ()=> {
                try {
                  const res = await fetch('/api/stripe/replies', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ amount: oneTimeAmount }) })
                  const data = await res.json();
                  if (data.url) {
                    window.location.href = data.url
                  }
                } catch (e) {
                  console.error(e)
                }
              }} className="px-6 h-11 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold">{t('pricing.proceed')}</button>
            </div>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl shadow-xl p-6 relative">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {confirmAction.upgrading ? t('pricing.confirmUpgradeTitle') : t('pricing.confirmDowngradeTitle')}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              {confirmAction.upgrading
                ? t('pricing.confirmUpgradeDesc')
                : t('pricing.confirmDowngradeDesc')}
            </p>
            {!confirmAction.upgrading && (
              <div className="text-xs text-amber-600 dark:text-amber-400 mb-4 space-y-1">
                <p>{t('pricing.downgradeEffectNote')}</p>
                <p>{t('pricing.downgradeApplyNextCycle')}</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 h-10 rounded-md bg-zinc-200 dark:bg-zinc-800 text-gray-800 dark:text-gray-200 text-sm font-medium hover:bg-zinc-300 dark:hover:bg-zinc-700"
              >
                {t('pricing.confirmCancel')}
              </button>
              <button
                onClick={() => {
                  const a = confirmAction; setConfirmAction(null); checkout(a.priceId, a.targetPlanId)
                }}
                className="px-5 h-10 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white text-sm font-semibold"
              >
                {confirmAction.upgrading ? t('pricing.confirmProceedUpgrade') : t('pricing.confirmProceedDowngrade')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
